import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { NetPingDeviceService } from '@zaoblako/web/core/services/control/net-ping-device.service';
import type { NetPingDeviceFindManyQueryInterface } from '@zaoblako/shared/modules/pacs/dtos/net-ping-device/net-ping-device-find-many-query.dto';
import type { NetPingDeviceFindInterface } from '@zaoblako/shared/modules/pacs/dtos/net-ping-device/net-ping-device-find.dto';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';

@Component({
  selector: 'crm-net-ping-device-single-select',
  standalone: true,
  imports: [CommonModule, SpinnerComponent, SingleSelectComponent],
  templateUrl: './net-ping-device-single-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NetPingDeviceSingleSelectComponent implements OnInit {
  @ViewChild('singleSelectComponent', { static: true }) public singleSelectComponent: SingleSelectComponent;

  @Input() public netPingDevices: NetPingDeviceFindInterface[] = [];
  public queryParams: NetPingDeviceFindManyQueryInterface = {
    limit: 40,
    offset: 0,
    select: ['id', 'name']
  };

  public loading = false;
  public paginationLoading = false;
  public count = 0;

  public _selectedNetPingDevice: NetPingDeviceFindInterface;
  @Input() public set selectedNetPingDevice(selectedNetPingDevice: NetPingDeviceFindInterface) {
    if (this.customSearch) {
      this._selectedNetPingDevice = selectedNetPingDevice;
      return;
    }

    if (this.netPingDevices.some((netPingDevice) => netPingDevice.id === selectedNetPingDevice?.id)) {
      this._selectedNetPingDevice = this.netPingDevices.find((netPingDevice) => netPingDevice.id === selectedNetPingDevice.id);
      return;
    }

    if (selectedNetPingDevice?.id) {
      if (typeof selectedNetPingDevice.id === 'string') {
        selectedNetPingDevice.id = +selectedNetPingDevice.id;
      }

      this.netPingDeviceService.findById<NetPingDeviceFindInterface>(selectedNetPingDevice.id).subscribe({
        next: (_netPingDevice) => {
          if (!this.netPingDevices.some((netPingDevice) => netPingDevice.id === _netPingDevice.id)) {
            this.netPingDevices = this.netPingDevices.concat(_netPingDevice);
            this.cdRef.markForCheck();
          }
          setTimeout(() => {
            this._selectedNetPingDevice = _netPingDevice;
            this.cdRef.markForCheck();
          }, 30);
        }
      });
    }
  }

  @Output() public selectedNetPingDeviceChange = new EventEmitter<NetPingDeviceFindInterface>();

  @Input() public isError = false;

  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public disabled: boolean;
  @Input() public appendTo: string;
  @Input() public showClear = true;
  @Input() public labelInput = false;

  @Input() public customSearch = false;

  @Input() public set setSelectedNetPingDeviceId(id: number) {
    if (id === null || id === undefined) {
      return;
    }

    let _id = id;
    if (typeof id === 'string') {
      _id = +id;
    }

    if (this.netPingDevices.some((netPingDevice) => netPingDevice.id === _id)) {
      this._selectedNetPingDevice = this.netPingDevices.find((netPingDevice) => netPingDevice.id === _id);
      return;
    }

    this.netPingDeviceService.findById<NetPingDeviceFindInterface>(_id).subscribe({
      next: (_netPingDevice) => {
        if (!this.netPingDevices.some((netPingDevice) => netPingDevice.id === _netPingDevice.id)) {
          this.netPingDevices = this.netPingDevices.concat(_netPingDevice);
          this.cdRef.markForCheck();
        }
        setTimeout(() => {
          this._selectedNetPingDevice = _netPingDevice;
          this.cdRef.markForCheck();
        }, 30);
      }
    });
  }

  public constructor(
    private readonly netPingDeviceService: NetPingDeviceService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    if (!this.customSearch) {
      this.loading = true;
      this.cdRef.markForCheck();

      this.netPingDeviceService.findMany(this.queryParams).subscribe({
        next: (data) => {
          this.netPingDevices = this.netPingDevices?.length
            ? this.netPingDevices.concat(
                data.items.filter((item) => !this.netPingDevices.some((netPingDevice) => netPingDevice.id === item.id))
              )
            : data.items.filter((item) => !this.netPingDevices.some((netPingDevice) => netPingDevice.id === item.id));
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public onSelect(netPingDevice: NetPingDeviceFindInterface) {
    this.selectedNetPingDeviceChange.emit(netPingDevice);
  }

  public onShowPanel() {
    if (!this.customSearch) {
      setTimeout(() => {
        this.singleSelectComponent?.dropdown?.scroller.onScroll.subscribe(() => {
          const el: HTMLElement = this.singleSelectComponent?.dropdown?.scroller?.elementViewChild?.nativeElement;
          if (el.offsetHeight + Math.ceil(el.scrollTop) >= el.scrollHeight) {
            if (this.netPingDevices.length < this.count && !this.loading && !this.paginationLoading) {
              this.paginationLoading = true;
              this.queryParams.offset += this.queryParams.limit;
              this.cdRef.markForCheck();

              this.netPingDeviceService.findMany(this.queryParams).subscribe({
                next: (data) => {
                  this.netPingDevices = this.netPingDevices.concat(
                    data.items.filter((item) => !this.netPingDevices.some((netPingDevice) => netPingDevice.id === item.id))
                  );
                  this.count = data.count;
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                },
                error: () => {
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                }
              });
            }
          }
        });
      }, 100);
    }
  }

  public search(name: string) {
    if (!this.customSearch) {
      this.loading = true;
      this.queryParams.search = name;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();

      this.netPingDeviceService.findMany(this.queryParams).subscribe({
        next: (data) => {
          if (!data.items.some((item) => item.id === this._selectedNetPingDevice?.id) && this._selectedNetPingDevice) {
            this.netPingDevices = data.items.concat(this._selectedNetPingDevice);
          } else {
            this.netPingDevices = data.items;
          }
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }
}
